import { useAuthStore, type AuthStatus } from './store';

import type { CurrentUser } from '../api/types';

export const useAuthStatus = (): AuthStatus => useAuthStore((s) => s.status);

/** The signed-in user, or null before the session is established. */
export const useCurrentUserOrNull = (): CurrentUser | null => useAuthStore((s) => s.user);

/**
 * The signed-in user. Only call inside the authenticated shell (below `RequireAuth`),
 * where a user is guaranteed.
 */
export function useCurrentUser(): CurrentUser {
  const user = useAuthStore((s) => s.user);
  if (!user) throw new Error('useCurrentUser() must be used inside the authenticated area (RequireAuth).');
  return user;
}

export const useIsAdmin = (): boolean => useAuthStore((s) => s.user?.isAdmin ?? false);

/** The account is an administrator, but not from this network (the security policy's allow-list): the role is switched off here. */
export const useAdminRestricted = (): boolean => useAuthStore((s) => s.user?.adminRestricted ?? false);

const NONE: readonly string[] = [];

/**
 * Whether the user holds one of `ids` (an administrator holds every permission). Pure, so tests and non-React code can use it;
 * `scoped` also lets people pass who hold a permission only for some departments (their screen opens, the server checks the
 * target of each action).
 */
export function holdsPermission(
  user: Pick<CurrentUser, 'isAdmin' | 'permissions' | 'scopedPermissions'> | null | undefined,
  ids: string | readonly string[],
  options: { scoped?: boolean } = {},
): boolean {
  if (!user) return false;
  if (user.isAdmin) return true;
  const wanted = typeof ids === 'string' ? [ids] : ids;
  const held = user.permissions ?? NONE;
  if (wanted.some((id) => held.includes(id))) return true;
  if (!options.scoped) return false;
  const limited = user.scopedPermissions ?? NONE;
  return wanted.some((id) => limited.includes(id));
}

/** The permissions the server expanded from the user's roles (every id for an administrator; empty before sign-in). */
export const usePermissions = (): readonly string[] => useAuthStore((s) => s.user?.permissions ?? NONE);

/** Whether the signed-in user holds one of the permissions (unlimited; someone limited to departments does not count). */
export const useHasPermission = (ids: string | readonly string[]): boolean =>
  useAuthStore((s) => holdsPermission(s.user, ids));

/**
 * Like {@link useHasPermission}, but with `{ scoped: true }` somebody who holds a permission only for some departments passes
 * too: for the screens that open to them while the server checks each target.
 */
export const useHasAnyPermission = (
  ids: string | readonly string[],
  options: { scoped?: boolean } = {},
): boolean => useAuthStore((s) => holdsPermission(s.user, ids, options));
